import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Mail, Plus, RefreshCw, Send, TrendingUp,
} from 'lucide-react';
import ManyChatCard from './ManyChatCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CampaignsTab() {
  const token = sessionStorage.getItem('admin_token');
  const [stats, setStats] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [v2Sending, setV2Sending] = useState(false);
  const [v2Result, setV2Result] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [v2TestEmail, setV2TestEmail] = useState('');
  const [sendLimit, setSendLimit] = useState(0);
  const [v2Limit, setV2Limit] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setStats(await res.json());
    } catch (err) { console.error('Fetch campaign stats failed:', err); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const importCsvs = async () => {
    setImporting(true); setImportResult(null);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/import-server-csvs`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setImportResult(data);
      fetchStats();
    } catch (e) { setImportResult({ error: e.message }); }
    finally { setImporting(false); }
  };

  // Legacy send (peptide_interest only)
  const sendTestEmail = async () => {
    if (!testEmail.trim()) return;
    setSending(true); setSendResult(null);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/send-peptiq`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ test_only: true, test_email: testEmail.trim() }),
      });
      setSendResult(await res.json());
    } catch (e) { setSendResult({ status: 'error', error: e.message }); }
    finally { setSending(false); }
  };

  const sendCampaign = async () => {
    if (sending) return;
    if (!window.confirm(`Send PeptIQ email to ${sendLimit > 0 ? sendLimit : 'ALL'} peptide-interest leads?`)) return;
    setSending(true); setSendResult(null);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/send-peptiq`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tag: 'peptide_interest', limit: sendLimit }),
      });
      setSendResult(await res.json());
      fetchStats();
    } catch (e) { setSendResult({ status: 'error', error: e.message }); }
    finally { setSending(false); }
  };

  // V2: ManyChat + peptide_interest
  const sendV2Test = async () => {
    if (!v2TestEmail.trim()) return;
    setV2Sending(true); setV2Result(null);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/send-peptiq-v2`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ test_only: true, test_email: v2TestEmail.trim() }),
      });
      setV2Result(await res.json());
    } catch (e) { setV2Result({ status: 'error', error: e.message }); }
    finally { setV2Sending(false); }
  };

  const sendV2Campaign = async () => {
    if (v2Sending) return;
    const totalAudience = (stats?.manychat_leads || 0) + (stats?.peptide_interest_leads || 0);
    const alreadySent = stats?.peptiq_v2_sent || 0;
    const eligible = totalAudience - alreadySent;
    if (!window.confirm(`Send PeptIQ LAUNCH email (20% off, code NINJA) to ${v2Limit > 0 ? v2Limit : eligible} contacts? This includes ALL ManyChat + peptide interest leads.`)) return;
    setV2Sending(true); setV2Result(null);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/send-peptiq-v2`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ limit: v2Limit }),
      });
      setV2Result(await res.json());
      fetchStats();
    } catch (e) { setV2Result({ status: 'error', error: e.message }); }
    finally { setV2Sending(false); }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading campaign data...</div>;

  return (
    <div className="space-y-6" data-testid="campaigns-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {[
          { label: 'Total Leads', val: stats?.total_leads || 0, color: 'text-blue-600' },
          { label: 'Peptide Interest', val: stats?.peptide_interest_leads || 0, color: 'text-emerald-600' },
          { label: 'ManyChat Leads', val: stats?.manychat_leads || 0, color: 'text-cyan-600' },
          { label: 'PeptIQ v2 Sent', val: stats?.peptiq_v2_sent || 0, color: 'text-violet-600' },
          { label: 'Drip Pending (5d)', val: stats?.peptiq_drip_pending || 0, color: 'text-amber-500' },
          { label: 'Re-engagement Sent', val: stats?.reengagement_sent || 0, color: 'text-amber-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* ManyChat sync + interest report */}
      <ManyChatCard />

      {/* CSV Import */}
      <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="csv-import-section">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <FileText className="w-4 h-4" /> Import ManyChat Contacts
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Import all CSV files from ManyChat peptide automations. Deduplicates automatically and tags as "peptide_interest".
        </p>
        <button onClick={importCsvs} disabled={importing}
          className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-testid="import-csvs-btn">
          {importing ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Importing...</> : <><Plus className="w-3.5 h-3.5" /> Import All CSVs</>}
        </button>
        {importResult && !importResult.error && (
          <div className="mt-3 bg-emerald-50 text-emerald-700 p-4 rounded-lg text-sm" data-testid="import-result">
            <p className="font-semibold mb-2">Import Complete</p>
            <p>{importResult.total_imported} new contacts imported &bull; {importResult.total_duplicates_updated} duplicates updated &bull; {importResult.total_skipped} invalid skipped</p>
            <div className="mt-2 space-y-1">
              {(importResult.files || []).map((f, i) => (
                <p key={f.duplicates} className="text-xs text-emerald-600">{f.file}: +{f.imported} new, {f.duplicates} dupes, {f.skipped} skipped</p>
              ))}
            </div>
          </div>
        )}
        {importResult?.error && (
          <div className="mt-3 bg-red-50 text-red-600 p-3 rounded-lg text-sm">Error: {importResult.error}</div>
        )}
      </div>

      {/* ═══ PeptIQ V2 LAUNCH CAMPAIGN ═══ */}
      <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl border border-yellow-400/30 p-6" data-testid="peptiq-v2-campaign-section">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="bg-yellow-400 text-black text-xs font-black px-2 py-0.5 uppercase tracking-wider">NEW</span>
              <h3 className="font-bold text-white text-base flex items-center gap-2">
                <Mail className="w-4 h-4 text-yellow-400" /> PeptIQ Launch Email — v2
              </h3>
            </div>
            <p className="text-xs text-gray-400">
              Sends to <strong className="text-gray-300">ALL ManyChat leads + peptide interest subscribers</strong>. Includes: 20% off annual ($80/yr), coupon <strong className="text-yellow-400">NINJA</strong>, app screenshots, comparison table, and partner program.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
            <p className="text-yellow-400 text-xl font-bold">{stats?.manychat_leads || 0}</p>
            <p className="text-gray-500 text-xs">ManyChat Leads</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
            <p className="text-emerald-400 text-xl font-bold">{stats?.peptide_interest_leads || 0}</p>
            <p className="text-gray-500 text-xs">Peptide Interest</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
            <p className="text-violet-400 text-xl font-bold">{stats?.peptiq_v2_sent || 0}</p>
            <p className="text-gray-500 text-xs">Already Sent</p>
          </div>
        </div>

        {/* Test send */}
        <div className="flex items-center gap-3 mb-3">
          <input type="email" value={v2TestEmail} onChange={e => setV2TestEmail(e.target.value)}
            placeholder="Preview: send test to your email..."
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-yellow-400/50 focus:outline-none"
            data-testid="v2-test-email-input" />
          <button onClick={sendV2Test} disabled={!v2TestEmail.trim() || v2Sending}
            className="px-4 py-2 bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap"
            data-testid="v2-send-test-btn">
            Send Preview
          </button>
        </div>

        {/* Full send */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Limit:</label>
            <input type="number" value={v2Limit} onChange={e => setV2Limit(parseInt(e.target.value) || 0)} min={0} placeholder="0 = all"
              className="w-20 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none" />
          </div>
          <button onClick={sendV2Campaign} disabled={v2Sending}
            className="flex-1 px-5 py-2.5 bg-yellow-400 text-black text-sm font-bold rounded-lg hover:bg-yellow-300 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="send-v2-campaign-btn">
            {v2Sending ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Sending...</> : <><Send className="w-3.5 h-3.5" /> Send PeptIQ Launch Email (v2)</>}
          </button>
        </div>

        {v2Result && (
          // eslint-disable-next-line no-nested-ternary -- readable tier picker
          <div className={`mt-3 p-3 rounded-lg text-sm ${v2Result.status === 'sending' || v2Result.status === 'test_sent' ? 'bg-emerald-900/40 text-emerald-400 border border-emerald-800' : v2Result.status === 'no_eligible' ? 'bg-amber-900/40 text-amber-400 border border-amber-800' : 'bg-red-900/40 text-red-400 border border-red-800'}`} data-testid="v2-campaign-result">
            {v2Result.status === 'sending' && `Campaign started! Sending to ${v2Result.total_eligible} contacts (out of ${v2Result.total_in_audience} total audience). ${v2Result.already_sent} already received it. Refresh to track progress.`}
            {v2Result.status === 'test_sent' && `Preview sent to ${v2TestEmail}! ${v2Result.result?.status === 'sent' ? 'Check your inbox.' : `Error: ${v2Result.result?.error}`}`}
            {v2Result.status === 'no_eligible' && `All ${v2Result.already_sent || 0} contacts have already received the v2 email.`}
            {v2Result.status === 'error' && `Error: ${v2Result.error}`}
          </div>
        )}
      </div>

      {/* Legacy PeptIQ Campaign */}
      <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="peptiq-campaign-section">
        <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Mail className="w-4 h-4 text-gray-400" /> PeptIQ Campaign (Legacy — Peptide Interest Only)
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Original PeptIQ email sent to peptide-interest tag only. Use the v2 campaign above for the full audience.
        </p>
        <div className="flex items-center gap-3 mb-3">
          <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Send test to..."
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" data-testid="test-email-input" />
          <button onClick={sendTestEmail} disabled={!testEmail.trim() || sending}
            className="px-4 py-2 bg-gray-600 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 disabled:opacity-50" data-testid="send-test-btn">
            Send Test
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Limit:</label>
            <input type="number" value={sendLimit} onChange={e => setSendLimit(parseInt(e.target.value) || 0)} min={0} placeholder="0 = all"
              className="w-20 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
          </div>
          <button onClick={sendCampaign} disabled={sending}
            className="px-5 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2" data-testid="send-campaign-btn">
            {sending ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Sending...</> : <><Send className="w-3.5 h-3.5" /> Send Campaign</>}
          </button>
        </div>
        {sendResult && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${sendResult.status === 'completed' || sendResult.status === 'test_sent' || sendResult.status === 'sending' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`} data-testid="campaign-result">
            {sendResult.status === 'completed' && `Campaign sent! ${sendResult.sent} delivered, ${sendResult.failed} failed.`}
            {sendResult.status === 'sending' && `Campaign started! Sending to ${sendResult.total_eligible} contacts in background.`}
            {sendResult.status === 'test_sent' && `Test sent! ${sendResult.result?.status === 'sent' ? 'Check your inbox.' : `Error: ${sendResult.result?.error}`}`}
            {sendResult.status === 'no_eligible' && 'No eligible contacts remaining.'}
            {sendResult.status === 'error' && `Error: ${sendResult.error}`}
          </div>
        )}
      </div>

      {/* Re-engagement Campaign */}
      <ReengagementSection token={token} onRefresh={fetchStats} />

      {/* Campaign History */}
      {(stats?.campaigns || []).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Campaign History</h3>
          <div className="space-y-2">
            {stats.campaigns.map((c, i) => (
              <div key={c.type} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2">
                <div>
                  <p className="text-gray-700 font-medium">{c.type} &mdash; {c.campaign_id}</p>
                  <p className="text-xs text-gray-400">{c.started_at ? new Date(c.started_at).toLocaleString() : ''} &bull; Status: {c.status}</p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-emerald-600">{c.sent} sent</span>
                  {c.failed > 0 && <span className="text-red-500">{c.failed} failed</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



function ReengagementSection({ token, onRefresh }) {
  const [daysInactive, setDaysInactive] = useState(30);
  const [testEmail, setTestEmail] = useState('');
  const [sendLimit, setSendLimit] = useState(0);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const sendTest = async () => {
    if (!testEmail.trim()) return;
    setSending(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/send-reengagement`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ test_only: true, test_email: testEmail.trim() }),
      });
      setResult(await res.json());
    } catch (e) { setResult({ status: 'error', error: e.message }); }
    finally { setSending(false); }
  };

  const sendCampaign = async () => {
    if (sending) return;
    if (!window.confirm(`Send re-engagement email to inactive leads (${daysInactive}+ days)?`)) return;
    setSending(true); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/send-reengagement`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ days_inactive: daysInactive, limit: sendLimit }),
      });
      setResult(await res.json());
      if (onRefresh) onRefresh();
    } catch (e) { setResult({ status: 'error', error: e.message }); }
    finally { setSending(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="reengagement-section">
      <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
        <TrendingUp className="w-4 h-4" /> Re-engagement Campaign
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Send "We miss you — 15% off" emails to leads who haven't been emailed in X days. Each lead only receives it once.
      </p>

      {/* Test */}
      <div className="flex items-center gap-3 mb-4">
        <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Send test to..."
          className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" data-testid="reengagement-test-email" />
        <button onClick={sendTest} disabled={!testEmail.trim() || sending}
          className="px-4 py-2 bg-gray-600 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 disabled:opacity-50" data-testid="reengagement-test-btn">
          Send Test
        </button>
      </div>

      {/* Full Send */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Days inactive:</label>
          <input type="number" value={daysInactive} onChange={e => setDaysInactive(parseInt(e.target.value) || 30)} min={7}
            className="w-16 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Limit:</label>
          <input type="number" value={sendLimit} onChange={e => setSendLimit(parseInt(e.target.value) || 0)} min={0}
            className="w-20 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
        </div>
        <button onClick={sendCampaign} disabled={sending}
          className="px-5 py-2.5 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2" data-testid="reengagement-send-btn">
          {sending ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Sending...</> : <><Send className="w-3.5 h-3.5" /> Send Re-engagement</>}
        </button>
      </div>

      {result && (
        // eslint-disable-next-line no-nested-ternary -- readable tier picker
        <div className={`mt-3 p-3 rounded-lg text-sm ${result.status === 'sending' || result.status === 'test_sent' ? 'bg-emerald-50 text-emerald-700' : result.status === 'no_eligible' ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-600'}`} data-testid="reengagement-result">
          {result.status === 'sending' && `Re-engagement started! Sending to ${result.total_eligible} inactive leads in background.`}
          {result.status === 'test_sent' && `Test sent! ${result.result?.status === 'sent' ? 'Check your inbox.' : `Error: ${result.result?.error}`}`}
          {result.status === 'no_eligible' && 'No eligible leads found — everyone has been engaged recently or already received this campaign.'}
          {result.status === 'error' && `Error: ${result.error}`}
        </div>
      )}
    </div>
  );
}
